import React, { useState, useEffect, useRef } from 'react';
import { useDevice } from '../../context/DeviceContext';
import './ProfileDropdown.css';

/** 板载配置数量（与参考 UI 一致） */
const ONBOARD_PROFILES = [1, 2, 3, 4];

const ChipIcon = ({ num }) => (
  <span className="profile-chip" aria-hidden>
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
      <rect
        x="6"
        y="6"
        width="12"
        height="12"
        rx="2.5"
        stroke="currentColor"
        strokeWidth="1.6"
      />
      <path
        d="M9 3.5v2.5M12 3.5v2.5M15 3.5v2.5M9 18v2.5M12 18v2.5M15 18v2.5M3.5 9h2.5M3.5 12h2.5M3.5 15h2.5M18 9h2.5M18 12h2.5M18 15h2.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
    <span className="profile-chip-num">{num}</span>
  </span>
);

/**
 * 右上角配置文件下拉
 * 板载配置 + 自定义配置分区
 */
const ProfileDropdown = ({
  selectedProfile,
  handleProfileChange,
  switchingProfile = null,
  variant = 'default',
  triggerPrefix = '',
}) => {
  const { dataStore } = useDevice();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    // 仍订阅设备数据，便于后续扩展（如自定义配置同步）
    if (!dataStore) return undefined;
    return undefined;
  }, [dataStore]);

  useEffect(() => {
    const onOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [open]);

  const onSelect = (num) => {
    setOpen(false);
    if (num !== selectedProfile) {
      handleProfileChange(num);
    }
  };

  const currentLabel =
    switchingProfile !== null
      ? `正在切换到板载配置${switchingProfile}…`
      : selectedProfile >= 1 && selectedProfile <= 4
      ? `板载配置${selectedProfile}`
      : `配置文件 ${selectedProfile}`;

  return (
    <div className={`profile-dropdown ${variant} ${open ? 'open' : ''}`} ref={ref}>
      <button
        type="button"
        className="profile-dropdown-trigger"
        onClick={() => setOpen((v) => !v)}
        disabled={switchingProfile !== null}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-busy={switchingProfile !== null}
      >
        {switchingProfile !== null && <span className="profile-switch-spinner" aria-hidden />}
        <span className="profile-dropdown-name">{triggerPrefix}{currentLabel}</span>
        <span className={`profile-dropdown-chevron ${open ? 'up' : ''}`} aria-hidden>
          ‹
        </span>
      </button>

      {open && (
        <div className="profile-dropdown-panel" role="listbox">
          <div className="profile-panel-header">
            <span className="profile-panel-title">配置文件</span>
            <button
              type="button"
              className="profile-panel-manage"
              onClick={(e) => {
                e.stopPropagation();
                // 管理页后续接入；先收起菜单
                setOpen(false);
              }}
            >
              管理配置文件
              <span className="profile-panel-manage-arrow">›</span>
            </button>
          </div>

          <div className="profile-panel-section">
            <div className="profile-section-label">板载配置</div>
            <ul className="profile-section-list">
              {ONBOARD_PROFILES.map((num) => {
                const isActive = Number(selectedProfile) === num;
                return (
                  <li key={num}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={isActive}
                      className={`profile-onboard-item ${isActive ? 'active' : ''}`}
                      onClick={() => onSelect(num)}
                    >
                      <span className="profile-onboard-name">板载配置{num}</span>
                      <ChipIcon num={num} />
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="profile-panel-section">
            <div className="profile-section-label">自定义配置</div>
            <div className="profile-custom-empty">暂无自定义配置</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileDropdown;
